import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import client from '../API/API';
import { SkeletonTableRow } from '../components/ui/Skeleton';

// ── Helpers ───────────────────────────────────────────────────────────────────
const toYMD = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const today = () => toYMD(new Date());
const sevenDaysAgo = () => { const d = new Date(); d.setDate(d.getDate() - 7); return toYMD(d); };
const thisMonthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; };
const PAGE_SIZE = 31;

const Icon = ({ d, size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const SearchIcon   = 'M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z';
const DownloadIcon = 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3';
const TableIcon    = 'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18';
const ChartIcon    = 'M18 20V10 M12 20V4 M6 20v-6';

function PresetChip({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: 20,
      border: `1px solid ${active ? 'var(--accent)' : 'var(--border-subtle)'}`,
      background: active ? 'var(--accent-soft)' : 'transparent',
      color: active ? 'var(--accent)' : 'var(--ink-500)',
      fontFamily: 'var(--font-sans)', fontSize: 12, cursor: 'pointer',
      transition: 'all var(--t-fast)',
    }}>
      {label}
    </button>
  );
}

// ── Bar chart card ────────────────────────────────────────────────────────────
function ReportChart({ title, dates, values, delay = 0 }) {
  const nums = values.map(v => { const n = parseFloat(v); return isNaN(n) ? 0 : n; });
  const max  = Math.max(...nums, 0);
  const option = {
    tooltip: { trigger: 'axis', backgroundColor: '#fff', borderColor: '#E4E6EC', textStyle: { fontFamily: 'DM Sans', fontSize: 12 } },
    grid: { top: 30, left: 52, right: 16, bottom: 48 },
    xAxis: {
      type: 'category', data: dates,
      axisLabel: { rotate: 35, interval: 0, fontSize: 10, fontFamily: 'DM Mono', color: '#B6BACA' },
      axisLine: { lineStyle: { color: '#ECEDF1' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value', min: 0, max: max > 0 ? max * 1.15 : 10,
      splitLine: { lineStyle: { type: 'dashed', color: '#ECEDF1' } },
      axisLabel: { fontSize: 10, fontFamily: 'DM Mono', color: '#B6BACA' },
      axisLine: { show: false }, axisTick: { show: false },
    },
    series: [{
      name: title, type: 'bar', data: nums,
      itemStyle: { color: '#0897BE', borderRadius: [2, 2, 0, 0] },
      emphasis: { itemStyle: { color: '#0780A3' } },
      barMaxWidth: 32,
    }],
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay }}
      style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border-hair)',
        borderRadius: 'var(--r-lg)', padding: 'var(--card-pad)', boxShadow: 'var(--sh-sm)',
      }}
    >
      <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 13, color: 'var(--ink-900)', marginBottom: 8 }}>
        {title}
      </div>
      <ReactECharts option={option} style={{ height: 240 }} />
    </motion.div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function ReportPage() {
  const [machines,     setMachines]     = useState([]);
  const [machineId,    setMachineId]    = useState('');
  const [startDate,    setStartDate]    = useState(sevenDaysAgo());
  const [endDate,      setEndDate]      = useState(today());
  const [summary,      setSummary]      = useState([]);
  const [colNames,     setColNames]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [searched,     setSearched]     = useState(false);
  const [page,         setPage]         = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [viewMode,     setViewMode]     = useState('table');
  const [activePreset, setActivePreset] = useState('7d');

  useEffect(() => {
    client.get('/api/machines/').then(r => setMachines(r.data.machines || [])).catch(console.error);
  }, []);

  const handleSearch = async (nextPage = 1) => {
    if (!machineId || !startDate) return;
    setLoading(true);
    try {
      const res = await client.get('/api/reports/', {
        params: { machine_id: machineId, start_date: startDate, end_date: endDate || startDate, page: nextPage, page_size: PAGE_SIZE },
      });
      const rows = res.data.summary || [];
      setSummary(rows);
      setPage(res.data.page || nextPage);
      setTotalPages(res.data.total_pages || 1);
      setTotalRecords(res.data.total_records || 0);
      if (res.data.col_headers?.length) {
        setColNames(res.data.col_headers);
      } else if (rows.length > 0) {
        setColNames(rows[0].ordered_channels.map(c => c.name));
      }
      setSearched(true);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleExport = async () => {
    if (!machineId || !startDate) return;
    const token = localStorage.getItem('token');
    const res   = await fetch(`/api/reports/export/?machine_id=${machineId}&start_date=${startDate}&end_date=${endDate || startDate}`,
      { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: `report_${machineId}_${startDate}_${endDate}.xlsx` }).click();
    URL.revokeObjectURL(url);
  };

  const setPreset = (preset) => {
    setActivePreset(preset);
    const e = today();
    const s = preset === '7d' ? sevenDaysAgo() : preset === 'month' ? thisMonthStart() : toYMD(new Date());
    setStartDate(s); setEndDate(e);
  };

  // Per-column chart data
  const chartData = colNames.map(col => {
    const dates = [], values = [];
    summary.forEach(row => {
      const ch = (row.ordered_channels || []).find(c => c.name === col);
      dates.push(row.date);
      values.push(ch?.value ?? 0);
    });
    return { col, dates, values };
  });

  const thStyle = { padding: '10px 14px', fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--ink-500)', textAlign: 'left', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '11px 14px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-900)' };

  return (
    <div style={{ maxWidth: 1640, margin: '0 auto' }}>

      {/* ── Page head ── */}
      <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 25, letterSpacing: '-0.5px', color: 'var(--ink-900)', marginBottom: 4 }}>
          Report
        </h1>
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-500)' }}>Daily aggregated machine data</p>
      </motion.div>

      {/* ── Filter bar ── */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.05 }}
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-hair)', borderRadius: 'var(--r-lg)', padding: '16px 18px', marginBottom: 16, boxShadow: 'var(--sh-sm)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          {/* Machine */}
          <div>
            <label style={labelStyle}>Machine</label>
            <select value={machineId} onChange={e => setMachineId(e.target.value)} style={selectStyle}>
              <option value="">Select machine…</option>
              {machines.map(m => <option key={m.machine_id} value={m.machine_id}>{m.machine_name || m.machine_id}</option>)}
            </select>
          </div>

          {/* Start date */}
          <div>
            <label style={labelStyle}>From</label>
            <input type="date" value={startDate} max={today()} onChange={e => { setStartDate(e.target.value); setActivePreset(''); }}
              style={{ ...selectStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          </div>

          {/* End date */}
          <div>
            <label style={labelStyle}>To</label>
            <input type="date" value={endDate} max={today()} min={startDate} onChange={e => { setEndDate(e.target.value); setActivePreset(''); }}
              style={{ ...selectStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          </div>

          <button onClick={() => handleSearch(1)} disabled={!machineId || loading}
            style={{ ...btnStyle(!!machineId), alignSelf: 'flex-end' }}
            onMouseEnter={e => { if (machineId) e.currentTarget.style.background = 'var(--accent-hover)'; }}
            onMouseLeave={e => { if (machineId) e.currentTarget.style.background = 'var(--accent)'; }}>
            <Icon d={SearchIcon} size={14} />
            {loading ? 'Loading…' : 'Search'}
          </button>

          <button onClick={handleExport} disabled={!searched || summary.length === 0}
            style={{ ...exportBtnStyle(searched), alignSelf: 'flex-end', marginLeft: 'auto' }}>
            <Icon d={DownloadIcon} size={14} color={searched ? 'var(--online)' : 'var(--ink-300)'} />
            Export Excel
          </button>
        </div>

        {/* Presets */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <PresetChip label="Today"       active={activePreset === 'today'} onClick={() => setPreset('today')} />
          <PresetChip label="Last 7 days" active={activePreset === '7d'}    onClick={() => setPreset('7d')} />
          <PresetChip label="This month"  active={activePreset === 'month'} onClick={() => setPreset('month')} />
        </div>
      </motion.div>

      {/* ── Results header with Table/Chart toggle ── */}
      {searched && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)' }}>
            Machine data · Showing <strong style={{ color: 'var(--ink-900)', fontFamily: 'var(--font-mono)' }}>{totalRecords}</strong> days
          </span>
          {/* Toggle */}
          <div style={{ display: 'flex', background: 'var(--bg-inset)', borderRadius: 'var(--r-md)', padding: 3, gap: 3 }}>
            {[['table', TableIcon, 'Table'], ['chart', ChartIcon, 'Charts']].map(([v, ic, lb]) => (
              <button key={v} onClick={() => setViewMode(v)} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 12px', borderRadius: 6, border: 'none',
                background: viewMode === v ? 'var(--bg-surface)' : 'transparent',
                boxShadow: viewMode === v ? 'var(--sh-sm)' : 'none',
                color: viewMode === v ? 'var(--ink-900)' : 'var(--ink-500)',
                fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: viewMode === v ? 500 : 400,
                cursor: 'pointer', transition: 'all var(--t-fast)',
              }}>
                <Icon d={ic} size={13} />
                {lb}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Table view ── */}
      {searched && viewMode === 'table' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-hair)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-sm)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-raised)', borderBottom: '2px solid var(--border-strong)' }}>
                  <th style={{ ...thStyle, position: 'sticky', left: 0, background: 'var(--bg-raised)', zIndex: 2 }}>Date</th>
                  {colNames.map(c => <th key={c} style={thStyle}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => <SkeletonTableRow key={i} cols={Math.max(colNames.length + 1, 4)} />)
                  : summary.map((row, ri) => {
                      const isEven = ri % 2 === 0;
                      const vals = {};
                      (row.ordered_channels || []).forEach(c => { vals[c.name] = c.value; });
                      return (
                        <tr key={ri} style={{ background: isEven ? 'var(--bg-surface)' : 'var(--bg-raised)', borderBottom: '1px solid var(--border-hair)' }}>
                          <td style={{ ...tdStyle, position: 'sticky', left: 0, zIndex: 1, background: isEven ? 'var(--bg-surface)' : 'var(--bg-raised)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap' }}>
                            {row.date || '—'}
                          </td>
                          {colNames.map(c => (
                            <td key={c} style={{ ...tdStyle, textAlign: 'right' }}>
                              {vals[c] !== undefined ? String(vals[c]) : '—'}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                }
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* ── Chart view ── */}
      {searched && viewMode === 'chart' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 'var(--grid-gap)' }}>
          {chartData.map((cd, i) => (
            <ReportChart key={cd.col} title={cd.col} dates={cd.dates} values={cd.values} delay={i * 0.05} />
          ))}
          {chartData.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--ink-500)', fontFamily: 'var(--font-sans)', fontSize: 14 }}>
              No chart data available.
            </div>
          )}
        </div>
      )}

      {searched && !loading && summary.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--ink-500)', fontFamily: 'var(--font-sans)', fontSize: 14 }}>
          No data found for the selected range.
        </div>
      )}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const labelStyle = { display: 'block', fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 };
const selectStyle = { padding: '8px 12px', fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-900)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', outline: 'none', cursor: 'pointer', minWidth: 200 };
const btnStyle = (active) => ({ display: 'flex', alignItems: 'center', gap: 6, background: active ? 'var(--accent)' : 'var(--border-strong)', color: '#fff', border: 'none', borderRadius: 'var(--r-md)', padding: '9px 18px', fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 13, cursor: active ? 'pointer' : 'not-allowed', transition: 'background var(--t-fast)' });
const exportBtnStyle = (active) => ({ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', color: active ? 'var(--online)' : 'var(--ink-300)', border: `1px solid ${active ? 'var(--online-border)' : 'var(--border-hair)'}`, borderRadius: 'var(--r-md)', padding: '9px 16px', fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 13, cursor: active ? 'pointer' : 'not-allowed', transition: 'all var(--t-fast)' });
